/**
 * Dev-only bridge surfacing the dictation widget's analytics events in the main
 * window's inspector. The widget is a separate webview (own sessionStorage), so
 * it forwards each event via shared localStorage + cross-webview `storage`
 * events (same mechanism as useAuthTokenStorageSync).
 */

import { isTauri } from '@/tauri/utils';
import { DICTATION_WIDGET_WINDOW_LABEL } from '@heidi/plugin-core';
import { getCurrentWindow } from '@tauri-apps/api/window';

import { pushAnalyticsInspectorEvent } from './store';
import type { AnalyticsInspectorEvent } from './types';

const BRIDGE_KEY = 'analytics-inspector:widget-bridge';

type BridgedPayload = Omit<AnalyticsInspectorEvent, 'id' | 'timestamp'>;

let nonce = 0;

function isDictationWidgetWebview(): boolean {
  if (!isTauri()) return false;
  try {
    return getCurrentWindow().label === DICTATION_WIDGET_WINDOW_LABEL;
  } catch {
    return false;
  }
}

/** Widget-side: forward an event to the main window. Returns true if forwarded
 * (caller must then skip the local push — the widget has no inspector UI). */
export function forwardWidgetAnalyticsEvent(payload: BridgedPayload): boolean {
  if (typeof window === 'undefined' || !isDictationWidgetWebview()) return false;
  try {
    // nonce forces a value change so identical consecutive events still fire.
    nonce += 1;
    window.localStorage.setItem(BRIDGE_KEY, JSON.stringify({ nonce, payload }));
  } catch {
    // localStorage disabled / quota — dev tooling only, drop silently.
  }
  return true;
}

/** Main-window: replay widget-forwarded events into the inspector. */
export function subscribeToWidgetAnalyticsEvents(): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: StorageEvent) => {
    if (event.key !== BRIDGE_KEY || !event.newValue) return;
    try {
      const { payload } = JSON.parse(event.newValue) as { payload: BridgedPayload };
      pushAnalyticsInspectorEvent(payload);
    } catch {
      // ignore malformed payloads.
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
